import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MButton } from "@/components/ui/atoms/MButtons";
import { useSubscription } from "@/hooks/useSubscription";
import { useProfileUpdate, getMemberInfo } from "@/hooks/useMembers";
import MEmptyCard from "@/components/ui/organisms/MEmptyCard";
import { IconAlarm, IconCircleCheckFilled } from "@tabler/icons-react";
import emptyCardStyles from "@/components/ui/organisms/MEmptyCard.module.css";
import { formatDocumentDateShort } from "@/utils/date-utils";
import { useSelector } from "react-redux";
import type { RootState } from "@/store";
import { useFamily } from "@/hooks/useFamily";
import { memberNormalizer } from "@/utils/memberNormalizer";
import { triggerNativeIAPPurchase } from "@/utils/nativeBridge";
import { useIsDesktopPWA } from "@/hooks/useIsDesktopPWA";
import AppStoreBadges from "@/components/ui/atoms/AppStoreBadges";
import MLoader from "@/components/ui/atoms/MLoader";
import MAvatar from "@/components/ui/atoms/MAvatar";
import { useAvatar } from "@/hooks/useAvatar";
import MSpacer from "@/components/ui/atoms/MSpacer";
import MDialog from "@/components/ui/atoms/MDialog";
import Lottie from "lottie-react";
import successAnimation from "@/assets/animations/success.json";
import dashboardStyles from "../dashboard.module.css";
import styles from "./SubscriptionExpired.module.css";
import manageSubscriptionStyles from "@/pages/Subscription/ManageSubscription.module.css";
import { useTranslation } from "react-i18next";
import { trackUserDataLoaded } from "@/utils/gtm";
import {
  ANALYTICS_SCREEN_TYPES,
  ANALYTICS_SCREEN_NAMES,
} from "@/constants/analytics.constants";

const SubscriptionExpired: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const memberInfo = getMemberInfo(true);
  const { avatarUrl } = useAvatar();
  const {
    subscription: currentSubscription,
    plans,
    fetchPlans,
    fetchCurrentSubscription,
  } = useSubscription();
  const { updateMemberInLocalStorage } = useProfileUpdate();
  const isDesktopPWA = useIsDesktopPWA();
  const [selectedPlanId, setSelectedPlanId] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [lottieLoaded, setLottieLoaded] = useState(false);
  const { member, selectedMember } = useSelector((state: RootState) => ({
    member: state.auth?.member,
    selectedMember: state.auth?.selectedMember,
  }));
  const { data: familyMembers = [] } = useFamily();
  const displayMember = selectedMember || member;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        await Promise.all([fetchPlans(), fetchCurrentSubscription()]);
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [fetchPlans, fetchCurrentSubscription]);

  // Disable footer navigation when subscription is expired
  useEffect(() => {
    // Add class to body to disable footer
    document.body.classList.add("subscription-expired");

    return () => {
      // Remove class when component unmounts
      document.body.classList.remove("subscription-expired");
    };
  }, []);

  useEffect(() => {
    // Set the first non-internal plan as selected by default
    const nonInternalPlans = plans.filter((p) => !p.internalPlan);
    if (nonInternalPlans.length > 0 && !selectedPlanId) {
      setSelectedPlanId(nonInternalPlans[0].id);
    }
  }, [plans, selectedPlanId]);

  useEffect(() => {
    // Listen for IAP purchase results from native
    const handleMessage = (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data);

        if (data.type === "iap_purchase_result") {
          console.log("IAP Purchase Result:", data.payload);

          if (data.payload.success) {
            // Payment successful - show success modal
            setLottieLoaded(false); // Reset Lottie state
            setShowSuccessModal(true);
          } else {
            // Payment failed - stay on current page or show error
            console.error("Payment failed:", data.payload.error);
          }
        }
      } catch (error) {
        console.error("Error parsing message from native:", error);
      }
    };

    window.addEventListener("message", handleMessage);
    document.addEventListener("message", handleMessage as any);

    return () => {
      window.removeEventListener("message", handleMessage);
      document.removeEventListener("message", handleMessage as any);
    };
  }, [navigate]);

  const handleDeleteAccount = () => {
    // Match family index.tsx logic for FO self-delete
    const isSelf = true;
    const isFO = displayMember?.family_member_type === "fo";
    let eligibleForOwnership: any[] = [];
    if (isSelf && isFO && Array.isArray(familyMembers)) {
      eligibleForOwnership = familyMembers.filter(
        (m: any) =>
          m.registered_member &&
          (m.id || m._id) !== (displayMember.id || displayMember._id) &&
          m.family_member_type !== "fo"
      );
    }
    const normalizedMember = memberNormalizer(displayMember);
    navigate("/profile/family/delete-member", {
      state: {
        action: "fo-delete-registered",
        targetMember: normalizedMember,
        eligibleForOwnership,
        backUrl: "/dashboard",
      },
    });
  };

  const getPrimaryText = () => {
    if (memberInfo?.family_member_type === "fm") {
      return "Your family's subscription\nhas ended";
    } else {
      if (currentSubscription?.status === "expired") {
        const formattedDate = currentSubscription?.endDate
          ? formatDocumentDateShort(new Date(currentSubscription.endDate))
          : "";
        return `Your free trial period has ended on ${formattedDate}`;
      } else {
        return "Your monthly subscription has ended";
      }
    }
  };

  const getSecondaryText = () => {
    if (memberInfo?.family_member_type === "fm") {
      return (
        <>
          Ask your Family Owner to renew it
          <br />
          to continue using Maatra.
          <br />
          <br />
          If you need help, write to
          <br />
          <a
            href="mailto:support@maatra.health"
            className={styles.supportEmail}
          >
            support@maatra.health
          </a>
        </>
      );
    } else {
      if (currentSubscription?.status === "expired") {
        return "Subscribe to restore access\nto full features";
      } else {
        return "Subscribe again to restore access\nto full features";
      }
    }
  };

  if (isLoading) {
    return (
      <div className={styles.container}>
        <MLoader />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className="dashboard-ellipses">
        <div className="dashboard-ellipse dashboard-ellipse-1"></div>
        <div className="dashboard-ellipse dashboard-ellipse-2"></div>
        <div className="dashboard-ellipse dashboard-ellipse-3"></div>
        <div className="dashboard-ellipse dashboard-ellipse-4"></div>
        <div className="dashboard-ellipse dashboard-ellipse-5"></div>
      </div>
      {/* Top Profile Row */}
      <div className={dashboardStyles.dashboardProfileRow}>
        <div className={styles.avatarButton}>
          <MAvatar
            size="md"
            text={`${memberInfo.firstName || ""}${
              memberInfo.lastName ? " " + memberInfo.lastName : ""
            }`.trim()}
            type={memberInfo.avatar ? "dp" : "no-dp"}
            src={avatarUrl || undefined}
            alt={`${memberInfo.firstName || ""} ${
              memberInfo.lastName || ""
            }`.trim()}
            memberId={memberInfo.id}
          />
        </div>
      </div>

      {/* Greeting */}
      <div className={dashboardStyles.dashboardIntro}>
        Welcome {memberInfo.firstName || ""},<br />
        <span className={dashboardStyles.dashboardIntroMessage}>
          Maatra is here to support your family’s health.
        </span>
      </div>

      {/* Empty Card with warning */}
      <MEmptyCard
        icon={
          <div className={emptyCardStyles.iconContainer}>
            <IconAlarm size={45} color="#8764BB" />
          </div>
        }
        primaryTitle={getPrimaryText()}
        secondaryTitle={getSecondaryText()}
        className={emptyCardStyles.subscriptionVariant}
      />

      <MSpacer mt={10} />

      {/* Show subscription plans only for Family Owners */}
      {memberInfo?.family_member_type !== "fm" && (
        <>
          {isDesktopPWA ? (
            <>
              <MSpacer mt={32} />
              <p className={styles.mobileAppCta}>
                {t(
                  "subscription.subscriptionDetails.corporateMobileAppCtaLine1"
                )}
                <br />
                {t(
                  "subscription.subscriptionDetails.corporateMobileAppCtaLine2"
                )}
              </p>
              <MSpacer mt={24} />
              <AppStoreBadges />
              <MSpacer mt={10} />
            </>
          ) : (
            <>
              <h2 className={styles.sectionTitle}>Select plan</h2>

              <MSpacer mt={16} />

              <div className={styles.plansContainer}>
                {plans
                  .filter((p) => !p.internalPlan)
                  .map((plan) => (
                    <div
                      key={plan.id}
                      role="button"
                      tabIndex={0}
                      className={styles.planCard}
                    >
                      <div className={styles.planHeader}>
                        <div
                          className={styles.planContent}
                          style={{ whiteSpace: "nowrap" }}
                        >
                          <div className={styles.planTitle}>{plan.name}</div>
                          <div className={styles.priceRow}>
                            <span className={styles.price}>
                              ₹ {plan.products[0]?.price || 0}/
                              {plan.type === "monthly" ? "month" : "year"}
                            </span>
                          </div>
                        </div>
                        <div className={styles.planAction}>
                          <MButton
                            text="Choose plan"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();

                              if (!memberInfo) {
                                console.error("Member info missing");
                                return;
                              }

                              const product = plan.products?.[0];
                              if (!product) {
                                console.error("No product found for plan");
                                return;
                              }

                              const payload = {
                                memberId: memberInfo.id,
                                familyId: memberInfo.current_family,
                                planId: plan.id,
                                planName: plan.name,
                                planType: plan.type,
                                productId: product.productId,
                                price: product.price,
                                currency: "INR",
                              };

                              triggerNativeIAPPurchase(payload);
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
              </div>

              <MSpacer mt={10} />
            </>
          )}
        </>
      )}

      {/* Delete Account Button - Only for Family Owners */}
      {memberInfo?.family_member_type !== "fm" && (
        <div style={{ textAlign: "center" }}>
          <MButton
            variant="link"
            text="Delete account"
            size="lg"
            onClick={handleDeleteAccount}
          />
        </div>
      )}

      {/* Success Modal */}
      <MDialog
        isOpen={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false);
          globalThis.location.href = "/dashboard";
        }}
        title=""
        primaryCtaText={t("subscription.manageSubscription.successModal.cta")}
        onPrimaryCta={async () => {
          setShowSuccessModal(false);
          // Update local state and Redux (this internally calls doUpdateMember)
          await updateMemberInLocalStorage(memberInfo.id, memberInfo.onBehalf);
          trackUserDataLoaded({
            memberInfo,
            screenType: ANALYTICS_SCREEN_TYPES.INSTALL,
            screenName: ANALYTICS_SCREEN_NAMES.SUBSCRIPTION_SCREEN_NAME,
            action: ANALYTICS_SCREEN_NAMES.SUBSCRIPTION_ACTION,
          });
          // Force full page reload to fetch fresh subscription data
          globalThis.location.href = "/dashboard";
        }}
      >
        <div className={manageSubscriptionStyles.successModalContent}>
          <div className={manageSubscriptionStyles.lottieContainer}>
            <Lottie
              animationData={successAnimation}
              loop={false}
              autoplay={true}
              style={{ width: 200, height: 200 }}
              initialSegment={[0, 60]}
              onLoad={() => setLottieLoaded(true)}
              onComplete={() => {
                // Animation completed
              }}
              rendererSettings={{
                preserveAspectRatio: "xMidYMid meet",
                progressiveLoad: true,
                hideOnTransparent: true,
              }}
            />
            {!lottieLoaded && (
              <div
                style={{
                  position: "absolute",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "50%",
                }}
              >
                <IconCircleCheckFilled size={100} color="white" />
              </div>
            )}
          </div>
          <h2 className={manageSubscriptionStyles.successTitle}>
            {t("subscription.manageSubscription.successModal.title")}
          </h2>
          <p className={manageSubscriptionStyles.successDescription}>
            {t("subscription.manageSubscription.successModal.description")}
          </p>
        </div>
      </MDialog>
    </div>
  );
};

export default SubscriptionExpired;
